import React, { CSSProperties, useEffect, useState } from "react";
import { Form, Card, Steps, Button, Tag, Col, Row, Input, StepsProps, Popover, Divider, Badge } from "antd";
import VerticalCenter from "src/components/WrapCenter/VerticalCenter";
import { Rule } from "antd/es/form";
import { CheckCircleFilled, CheckOutlined } from "@ant-design/icons";
import "./ModalApprove.css";
import { BsFill1CircleFill, BsFill2CircleFill, BsFill3CircleFill, BsFill4CircleFill } from "react-icons/bs";
import {
  ApproveBody,
  DataApprove,
  ReportStatusEnum,
  StatusStepEnum,
  StepsApprove,
  multipleApprover,
} from "src/entity/voc-approve/voc-approve.entity";

const { Step } = Steps;

const ModalApproveStep = (props: {
  data: DataApprove;
  userId: string;
  approveId: string;
  email: string;
  name: string;
  onApprove: (approveBody: ApproveBody, action: string) => void;
}) => {
  const { data, userId, approveId, name, email, onApprove } = props;
  const [comment, setComment] = useState<string>("");
  const [form] = Form.useForm();
  const [action, setAction] = useState<string>("");

  const validateInputComment: Rule[] = [
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (action === StatusStepEnum.REJECT && !getFieldValue("comment")) {
          return Promise.reject(new Error("Please provide a reason"));
        }
        return Promise.resolve();
      },
    }),
  ];

  const onClick = async (status: "reject" | "approved") => {
    try {
      setAction(status);
      await form.validateFields(["comment"]);
      const approveBody: ApproveBody = {
        userId,
        comment,
        approveId,
        stepApprove: data.currentStep,
        status,
        name,
        email,
        year: data?.year,
        month: data?.month,
      };
      onApprove(approveBody, status);
    } catch (error) {
      console.error(error);
    }
  };

  const onChangeComment = (comment: string) => {
    setComment(comment);
  };

  const detailsCard = [
    {
      label: "Name",
      key: "approverName",
    },
    {
      label: "Email",
      key: "email",
    },
    {
      label: "Datetime",
      key: "datetime",
    },
  ];

  const mapStatusStep = [
    {
      label: "Request Approval",
      value: "request_approval",
    },
    {
      label: "Waiting for Approval",
      value: "waiting_for_approval",
    },

    {
      label: "Approved",
      value: "approved",
    },
    {
      label: "Reject",
      value: "reject",
    },
  ];

  const titleRender = (position: string, status: string) => {
    if (position != "Requester") {
      position = "Approvers";
    }

    let tagColor = "";
    switch (status) {
      case "approved":
        tagColor = "green";
        break;
      case "waiting_for_approval":
        tagColor = "orange";
        break;
      case "request_approval":
        tagColor = "purple";
        break;
      case "reject":
        tagColor = "red";
        break;
      default:
        tagColor = "default"; // Default color if status doesn't match specific cases
        break;
    }

    const labelStatus = mapStatusStep.find((item) => item.value === status);

    return (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Tag>{position}</Tag>
        <Tag color={tagColor}>{labelStatus?.label}</Tag>
      </div>
    );
  };

  const checkIconStep = (index: number) => {
    const iconStyle: CSSProperties = { fontSize: "35px", display: "inline-flex", alignItems: "center", textAlign: "center" };
    if (index === 1) {
      return <BsFill1CircleFill style={iconStyle} />;
    } else if (index === 2) {
      return <BsFill2CircleFill style={iconStyle} />;
    } else if (index === 3) {
      return <BsFill3CircleFill style={iconStyle} />;
    } else if (index === 4) {
      return <BsFill4CircleFill style={iconStyle} />;
    }
    return null;
  };

  const renderProgress = (stepName: string | JSX.Element, isLastStep: boolean, index: number, status: string) => (
    <>
      <div style={{ backgroundColor: "white", height: "100%" }}>
        {["approved", "reject", "request_approval"].includes(status) ? (
          <CheckCircleFilled style={{ fontSize: "35px" }} />
        ) : (
          checkIconStep(index + 1)
        )}
        <p style={{ fontSize: "13px", color: "black", fontWeight: "bold", wordWrap: "break-word", whiteSpace: "normal" }}>{stepName}</p>
        {!isLastStep && (
          <>
            <Divider type="vertical" style={{ borderColor: "gray", height: "260px" }} />
          </>
        )}
      </div>
    </>
  );

  const steps = data?.steps.map((item, index) => {
    let content;
    let title;
    let idxMatchMultiple: number = 0;
    let img = "";

    const { isMultipleApprover, stepNo, stepName, multipleApprover } = item;

    const isLastStep = index === data.steps.length - 1;

    if (stepNo === 1) {
      title = "Submit Report";
    } else {
      if (item.status === StatusStepEnum.APPROVED) {
        title = <p>{stepName}'s Approved</p>;
      } else if (item.status === StatusStepEnum.REJECT) {
        title = <p>{stepName}'s Reject</p>;
      } else {
        title = (
          <p>
            Waiting for
            <br />
            {stepName}'s Approval
          </p>
        );
      }
    }
    if (isMultipleApprover && multipleApprover.length > 0) {
      idxMatchMultiple = multipleApprover.findIndex((apv) => apv?.approverUserId === userId);
      if (idxMatchMultiple != -1) {
        img = `${process.env.REACT_APP_DEFAULT_IMG_URL}${item.multipleApprover[idxMatchMultiple].approverName}`;
      } else {
        idxMatchMultiple = 0;
        img = `${process.env.REACT_APP_DEFAULT_IMG_URL}${item.multipleApprover[idxMatchMultiple].approverName}`;
      }
    } else {
      img = `${process.env.REACT_APP_DEFAULT_IMG_URL}${item.approverName}`;
    }

    let isUserPermissionApprove;
    let isSameUser;
    if (isMultipleApprover && idxMatchMultiple != -1) {
      isUserPermissionApprove =
        data.currentStep === stepNo &&
        userId === item.multipleApprover[idxMatchMultiple]?.approverUserId &&
        item.status === StatusStepEnum.WAITING_FOR_APPROVE;
      isSameUser = userId === item.multipleApprover[idxMatchMultiple]?.approverUserId;
    } else {
      isUserPermissionApprove =
        data.currentStep === stepNo && userId === item?.approverUserId && item.status === StatusStepEnum.WAITING_FOR_APPROVE;
      isSameUser = userId === item?.approverUserId;
    }

    // console.log("Item & data : ", { item, data, isSameUser, isUserPermissionApprove });
    // console.log("Condition : ", {
    //   con1: item.status === StatusStepEnum.REJECT,
    //   con2: data.currentStep === stepNo && isSameUser && item.status != StatusStepEnum.APPROVED,
    // });
    if (item.status === StatusStepEnum.REJECT || (data.currentStep === stepNo && isSameUser && item.status != StatusStepEnum.APPROVED)) {
      content = (
        <Card style={{ width: "870px", backgroundColor: "#0099890D", height: "280px" }} title={titleRender(item.stepName, item.status)}>
          <Row gutter={[16, 16]} style={{ display: "flex", justifyContent: "space-between" }}>
            <Col span={3}>
              <VerticalCenter>
                <img alt="profile-img" src={img} className="rounded-[30px]" width={60} height={60} />
              </VerticalCenter>
            </Col>
            <Col span={10}>
              {detailsCard.map((detail, index) => (
                <Row key={index} style={{ marginBottom: 10 }}>
                  <Col span={6}>
                    <strong>{detail.label} :</strong>
                  </Col>
                  {isMultipleApprover && item.multipleApprover.length > 0 && idxMatchMultiple != -1 ? (
                    <Col span={18}>
                      <p>{item.multipleApprover[idxMatchMultiple][detail.key as keyof multipleApprover] ?? "-"} </p>
                    </Col>
                  ) : (
                    <Col span={18}>
                      <p>
                        {item[detail.key as keyof Omit<StepsApprove, "multipleApprover">]
                          ? item[detail.key as keyof Omit<StepsApprove, "multipleApprover">]
                          : "-"}
                      </p>
                    </Col>
                  )}
                </Row>
              ))}
            </Col>
            <Col span={11}>
              <strong>Comment</strong>
              <Form form={form}>
                <Form.Item name="comment" rules={validateInputComment}>
                  <Input.TextArea
                    disabled={!isUserPermissionApprove}
                    value={comment}
                    onChange={(e) => onChangeComment(e.target.value)}
                    rows={4}
                  />
                </Form.Item>

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Form.Item>
                    <Button
                      type="primary"
                      style={{
                        width: "170px",
                        backgroundColor: "#D83D0E",
                        borderColor: "red",
                        display: isUserPermissionApprove ? "block" : "none",
                      }}
                      color="red"
                      onClick={() => onClick("reject")}
                    >
                      Reject
                    </Button>
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      style={{
                        width: "170px",
                        display: isUserPermissionApprove ? "block" : "none",
                      }}
                      onClick={() => onClick("approved")}
                    >
                      Approve
                    </Button>
                  </Form.Item>
                </div>
              </Form>
            </Col>
          </Row>
        </Card>
      );
    } else {
      content = (
        <Card style={{ width: 550, backgroundColor: "#0099890D", height: "270px" }} title={titleRender(item.stepName, item.status)}>
          <Row gutter={[16, 16]} style={{ display: "flex", justifyContent: "space-between" }}>
            <Col span={5}>
              <VerticalCenter>
                <img alt="profile-img" src={img} className="rounded-[30px]" width={60} height={60} />
              </VerticalCenter>
            </Col>
            <Col span={19}>
              {detailsCard.map((detail, index) => (
                <Row key={index} style={{ marginBottom: 10 }}>
                  <Col span={5}>
                    <strong>{detail.label} :</strong>
                  </Col>
                  {isMultipleApprover && item.multipleApprover.length > 0 && idxMatchMultiple != -1 ? (
                    <Col span={19}>
                      <p>{item.multipleApprover[idxMatchMultiple][detail.key as keyof multipleApprover] ?? "-"} </p>
                    </Col>
                  ) : (
                    <Col span={19}>
                      <p>
                        {item[detail.key as keyof Omit<StepsApprove, "multipleApprover">]
                          ? item[detail.key as keyof Omit<StepsApprove, "multipleApprover">]
                          : "-"}
                      </p>
                    </Col>
                  )}
                </Row>
              ))}
              {item.stepName != "Requester" && (
                <Row style={{ marginBottom: 10 }}>
                  <Col span={5}>
                    <strong>Comment :</strong>
                  </Col>
                  <Col span={19}>{item.comment ? item.comment : "-"}</Col>
                </Row>
              )}
            </Col>
          </Row>
        </Card>
      );
    }

    return {
      title: title,
      content: content,
      status: item.status,
      isLastStep: isLastStep,
    };
  });

  useEffect(() => {
    const currentStepData = data.steps.find((step) => step.stepNo === data.currentStep);
    if (currentStepData) {
      setComment(currentStepData.comment);
      form.setFieldsValue({ comment: currentStepData.comment });
    }
  }, [data, form]);

  useEffect(() => {
    form.setFieldsValue({ comment: comment });
  }, [comment, form]);

  return (
    <Steps current={data.currentStep - 1} direction="vertical" type="default">
      {steps?.map((item, index) => (
        <Step
          className="custom-steps"
          key={index}
          description={item.content}
          icon={renderProgress(item.title, item.isLastStep, index, item.status)}
        />
      ))}
    </Steps>
  );
};

export default ModalApproveStep;
